import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Calendar, ChevronRight, ChevronLeft, Layers, Star, Newspaper } from 'lucide-react'; 
import { newsService, newsCategoryService } from '../../services/contentService'; 
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl, generateSlug } from '../../utils/helpers';

const ITEMS_PER_PAGE = 8; // Đưa hằng số ra ngoài component để tránh khởi tạo lại

const NewsPage = () => {
    const location = useLocation(); 
    
    const [newsList, setNewsList] = useState([]);
    const [categories, setCategories] = useState([]); 
    const [selectedCategory, setSelectedCategory] = useState(location.state?.categoryId || 'all'); 
    const [filterTime, setFilterTime] = useState('all'); 
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    // Cập nhật selectedCategory nếu route state thay đổi
    useEffect(() => {
        if (location.state?.categoryId) {
            setSelectedCategory(location.state.categoryId);
        }
    }, [location.state]);

    // Lấy và TIỀN XỬ LÝ dữ liệu (Chỉ chạy 1 lần)
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const [newsRes, catRes] = await Promise.all([
                    newsService.getPublicNews(),
                    newsCategoryService.getPublicCategories().catch(() => []) 
                ]);

                if (!isMounted) return;

                const rawNews = Array.isArray(newsRes?.data) ? newsRes.data : (Array.isArray(newsRes) ? newsRes : []);
                
                // Tiền xử lý: Tính toán timestamp và excerpt ngay từ đầu để không phải làm lại khi render
                const processedNews = rawNews.map(news => {
                    const timestamp = new Date(news.newsCreatedTime || news.createdAt || 0).getTime();
                    
                    let excerpt = news.newsExcerpt;
                    if (!excerpt) {
                        if (news.newsContent) {
                            const strippedText = news.newsContent.replace(/<[^>]*>?/gm, '');
                            excerpt = strippedText.length > 150 ? strippedText.substring(0, 150) + "..." : strippedText;
                        } else {
                            excerpt = "Đang cập nhật nội dung...";
                        }
                    }

                    return { ...news, _timestamp: timestamp, _excerpt: excerpt };
                }).sort((a, b) => b._timestamp - a._timestamp);

                setNewsList(processedNews);

                const catData = Array.isArray(catRes?.data) ? catRes.data : (Array.isArray(catRes) ? catRes : []);
                setCategories(catData);

            } catch (error) {
                console.error("Lỗi lấy dữ liệu:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, []);

    // Cache logic Filter bằng useMemo để không bị tính toán lại khi đổi trang (chuyển currentPage)
    const filteredNews = useMemo(() => {
        if (!newsList.length) return [];

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const startOfWeek = new Date(now.setDate(now.getDate() - 7)).getTime();
        const startOfMonth = new Date(now.setMonth(now.getMonth() - 1)).getTime();

        return newsList.filter(news => {
            // Lọc theo danh mục
            if (selectedCategory === 'featured') {
                if (!news.isFeatured) return false;
            } else if (selectedCategory !== 'all' && news.categoryId !== selectedCategory) {
                return false;
            }

            // Lọc theo thời gian (sử dụng timestamp đã pre-process để so sánh siêu tốc)
            if (filterTime === 'today') return news._timestamp >= startOfToday;
            if (filterTime === 'week') return news._timestamp >= startOfWeek;
            if (filterTime === 'month') return news._timestamp >= startOfMonth;
            
            return true;
        });
    }, [newsList, selectedCategory, filterTime]);

    // Reset về trang 1 khi đổi bộ lọc
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, filterTime]);

    const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE) || 1;
    
    // Cắt mảng hiển thị (cũng dùng useMemo để tối ưu)
    const paginatedNews = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredNews.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredNews, currentPage]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    };

    const getPaginationRange = () => {
        const delta = 2; 
        const range = [];
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            range.push(i);
        }
        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    };

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col font-inter">
            <Header /> 
            
            <div className="bg-gradient-to-r from-orange-200 to-white border-b border-orange-300/90">
                <div className="container mx-auto px-4 max-w-5xl py-8 flex flex-col items-center text-center">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-[#002855] mb-3 tracking-tight">Tin tức & Khuyến mãi</h1>
                    <p className="text-gray-500 max-w-2xl text-sm md:text-base">
                        Tổng hợp tin tức công nghệ mới nhất, chính sách và các chương trình khuyến mãi hấp dẫn từ Thành Nam.
                    </p>
                </div>
            </div>

            <main className="container mx-auto px-4 max-w-5xl py-8 flex-grow">
                {loading ? (
                    <div className="space-y-6">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="flex flex-col md:flex-row gap-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100 animate-pulse">
                                <div className="w-full md:w-64 h-40 bg-gray-200 rounded-lg"></div>
                                <div className="flex-1 space-y-4 py-2">
                                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="flex overflow-x-auto gap-2 pb-4 mb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <button
                                onClick={() => setSelectedCategory('all')}
                                className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all border ${
                                    selectedCategory === 'all' 
                                        ? 'bg-[#002855] text-white border-[#002855] shadow-md' 
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#002855] hover:text-[#002855]'
                                }`}
                            >
                                <Layers size={16} /> Tất cả tin tức
                            </button>

                            <button
                                onClick={() => setSelectedCategory('featured')}
                                className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all border ${
                                    selectedCategory === 'featured' 
                                        ? 'bg-tnc-orange text-white border-tnc-orange shadow-md shadow-orange-200' 
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-tnc-orange hover:text-tnc-orange'
                                }`}
                            >
                                <Star size={16} className={selectedCategory === 'featured' ? 'fill-white' : ''} /> Tin nổi bật
                            </button>

                            {categories.map(cat => (
                                <button
                                    key={cat.categoryId}
                                    onClick={() => setSelectedCategory(cat.categoryId)}
                                    className={`flex-shrink-0 px-5 py-2.5 rounded-full font-bold text-sm transition-all border ${
                                        selectedCategory === cat.categoryId 
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-600 hover:text-blue-600'
                                    }`}
                                >
                                    {cat.categoryName}
                                </button>
                            ))}
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 border-b border-gray-200 pb-4">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <div className={`w-1.5 h-6 rounded-full ${selectedCategory === 'featured' ? 'bg-tnc-orange' : 'bg-[#002855]'}`}></div> Danh sách bài viết
                            </h3>
                            
                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-orange-200/60 shadow-sm hover:border-tnc-orange transition-colors focus-within:border-tnc-orange">
                                <select 
                                    value={filterTime} 
                                    onChange={(e) => setFilterTime(e.target.value)}
                                    className="text-sm font-medium text-gray-700 outline-none bg-transparent cursor-pointer"
                                >
                                    <option value="all">Tất cả thời gian</option>
                                    <option value="today">Hôm nay</option>
                                    <option value="week">Trong tuần này</option>
                                    <option value="month">Trong tháng này</option>
                                </select>
                            </div>
                        </div>

                        {filteredNews.length > 0 ? (
                            <>
                                <div className="space-y-6">
                                    {paginatedNews.map(news => (
                                        <Link to={`/tin-tuc/${generateSlug(news.newsTitle)}-id${news.newsId}`} key={news.newsId} className="group flex flex-col md:flex-row gap-6 p-4 rounded-xl shadow-sm border border-orange-300/90 hover:shadow-md hover:border-tnc-orange hover:-translate-y-1 transition-all duration-300 relative bg-gradient-to-r from-orange-100 to-white">
                                            
                                            {news.categoryName && selectedCategory === 'all' && (
                                                <div className="absolute top-6 left-6 z-10">
                                                    <span className="bg-[#002855] text-white text-[10px] font-extrabold px-2.5 py-1 rounded shadow-sm uppercase tracking-wide">
                                                        {news.categoryName}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="w-full md:w-64 lg:w-72 h-44 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 relative">
                                                <img 
                                                    src={getImageUrl(news.newsImageUrl || news.newsImage)} 
                                                    alt={news.newsTitle} 
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                                                />
                                            </div>
                                            
                                            <div className="flex-1 flex flex-col py-1">
                                                <div className="flex items-center gap-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                                                    {news.isFeatured && <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded flex items-center gap-1"><Star size={10} className="fill-yellow-600"/> Nổi bật</span>}
                                                    <span className="text-tnc-orange bg-orange-50 px-2 py-0.5 rounded">Tin tức</span>
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={12} />
                                                        {new Date(news._timestamp).toLocaleDateString('vi-VN')}
                                                    </span>
                                                </div>
                                                
                                                <h3 className="font-bold text-gray-800 text-lg md:text-xl mb-2 line-clamp-2 leading-snug group-hover:text-tnc-orange transition-colors">
                                                    {news.newsTitle}
                                                </h3>
                                                
                                                <p className="text-sm text-gray-600 line-clamp-2 md:line-clamp-3 mb-4">
                                                    {news._excerpt}
                                                </p>
                                                
                                                <div className="text-[#002855] font-bold text-sm flex items-center gap-1 group-hover:text-tnc-orange transition-colors mt-auto">
                                                    Xem chi tiết <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 border-t border-orange-200/60 pt-6">
                                        <div className="text-sm text-gray-500 font-medium">
                                            Hiển thị <strong className="text-gray-900">{(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredNews.length)}</strong> trên tổng số <strong className="text-gray-900">{filteredNews.length}</strong> bài viết
                                        </div>

                                        <div className="flex flex-wrap items-center justify-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <button 
                                                    onClick={() => handlePageChange(currentPage - 1)} 
                                                    disabled={currentPage === 1}
                                                    className="px-3.5 py-2 rounded-lg border border-orange-200 bg-white text-gray-600 font-bold hover:bg-orange-50 hover:text-tnc-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                                >
                                                    <ChevronLeft size={16} /> <span className="hidden sm:inline">Trang trước</span>
                                                </button>

                                                <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] md:max-w-none scrollbar-hide">
                                                    {getPaginationRange().map((page, index) => {
                                                        if (page === "...") {
                                                            return <span key={`dots-${index}`} className="px-2 py-1 text-gray-400 font-bold">...</span>;
                                                        }
                                                        return (
                                                            <button 
                                                                key={index} 
                                                                onClick={() => handlePageChange(page)}
                                                                className={`w-10 h-10 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                                    ${currentPage === page 
                                                                        ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                                        : 'bg-white text-gray-600 border-orange-200 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                                    }`}
                                                            >
                                                                {page}
                                                            </button>
                                                        )
                                                    })}
                                                </div>

                                                <button 
                                                    onClick={() => handlePageChange(currentPage + 1)} 
                                                    disabled={currentPage === totalPages}
                                                    className="px-3.5 py-2 rounded-lg border border-orange-200 bg-white text-gray-600 font-bold hover:bg-orange-50 hover:text-tnc-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                                >
                                                    <span className="hidden sm:inline">Trang sau</span> <ChevronRight size={16} />
                                                </button>
                                            </div>

                                            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                                <span className="text-sm text-gray-500 font-medium">Đến trang:</span>
                                                <input 
                                                    key={`news-page-${currentPage}`}
                                                    type="number" min={1} max={totalPages} defaultValue={currentPage}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            const page = parseInt(e.target.value);
                                                            if (page >= 1 && page <= totalPages) handlePageChange(page);
                                                            else e.target.value = currentPage;
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const page = parseInt(e.target.value);
                                                        if (page >= 1 && page <= totalPages && page !== currentPage) handlePageChange(page);
                                                        else e.target.value = currentPage;
                                                    }}
                                                    className="w-16 h-10 px-2 text-center border border-orange-200 rounded-lg outline-none text-sm font-bold text-gray-700 focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm transition-all"
                                                    title="Nhập số trang và nhấn Enter"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-24 bg-gradient-to-b from-orange-50 to-white rounded-2xl border border-orange-200/60 shadow-sm">
                                <Newspaper size={48} className="mx-auto mb-4 text-gray-300" />
                                <h3 className="text-xl font-bold text-gray-700 mb-2">Không tìm thấy bài viết</h3>
                                <p className="text-gray-500">Chưa có tin tức nào phù hợp với bộ lọc hiện tại.</p>
                                <button onClick={() => { setFilterTime('all'); setSelectedCategory('all'); }} className="mt-4 text-tnc-orange font-bold hover:underline">
                                    Xóa bộ lọc và xem tất cả
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>
            
            <Footer /> 
        </div>
    );
};

export default NewsPage;